<script setup>
import { ref, computed, watch } from "vue";
import DefaultTooltip from "@/components/global-themed/DefaultTooltip.vue";
import GoogleConnectionModal from "@/components/seo-score/partials/modals/GoogleConnectionModal.vue";

const props = defineProps({
  name: {
    required: true,
    type: String,
  },
  icon: {
    required: true,
    type: String,
  },
  status: {
    required: true,
    type: String,
    validator: value => [
      'pending',
      'connected',
      'error',
    ].includes(value),
  },
  connectionUri: {
    required: true,
    type: String,
  },
});

const isOpen = ref(false);

const modalConfig = computed(() => {
  const configs = {
    pending: {
      title: 'Not Connected',
      description: `Connect Google ${props.name} so we can measure how people find and interact with your business on Google. This helps us improve your visibility score and show the impact of your SEO.`,
      buttonVariant: 'primary',
      buttonText: "Connect Now",
    },
    error: {
      title: 'Connection Error',
      description: `We couldn't connect to Google ${props.name}. Please reconnect so we can continue tracking how customers find your business on Google.`,
      buttonVariant: 'danger',
      buttonText: "Reconnect",
    },
    connected: {
      title: 'Connected',
      description: `Google ${props.name} is connected and we're now tracking how people find your business on Google. This data helps us measure your visibility and identify opportunities to attract more customers.`,
    },
  };

  return configs[props.status] || configs.pending;
});

watch(isOpen, () => {
  if (!isOpen.value) {
    return;
  }

  window.mixpanel('SEO Score: Viewed Google Connection', {
    name: props.name,
  });
});

</script>

<template>
  <DefaultTooltip
    v-model:shown="isOpen"
    :triggers="['hover']"
    :delay="{ show: 200, hide: 100 }"
    placement="bottom"
  >
    <template #trigger>
      <div
        class="tw-flex tw-flex-col tw-gap-3.5 tw-rounded-xl tw-p-3 tw-items-center tw-cursor-pointer tw-transition-all hover:tw-shadow-md"
        :class="{
          'tw-bg-caution-100': status === 'error',
        }"
      >
        <div
          class="tw-h-1.5 tw-rounded tw-w-full"
          :class="{
            'tw-bg-neutral-100' : status === 'pending',
            'tw-bg-success-700' : status === 'connected',
            'tw-bg-caution' : status === 'error',
          }"
        />
        <div class="tw-flex tw-items-center tw-gap-2">
          <svg-vue
            :icon="icon"
            class="tw-w-5 tw-h-5 tw-shrink-0"
          />
          <div class="tw-flex tw-gap-[3px] tw-items-stretch">
            <svg-vue
              icon="icons/google"
              class="tw-h-3.5 tw-text-neutral-900"
            />
            <span class="tw-text-neutral-700 tw-text-xs tw-leading-none">{{ name }}</span>
          </div>
        </div>
      </div>
    </template>

    <template #content>
      <GoogleConnectionModal
        :name="name"
        :title="modalConfig.title"
        :description="modalConfig.description"
        :button-variant="modalConfig.buttonVariant"
        :button-text="modalConfig.buttonText"
        :connection-uri="status !== 'connected' ? connectionUri : undefined"
      />
    </template>
  </DefaultTooltip>
</template>
